import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ServiceNotice } from '@gc/shared';

import {
  api,
  deadline,
  getAccessToken,
  isSessionRejected,
  isVenueSuspended,
  onSessionEnded,
  onSessionRenewed,
  onVenueSuspended,
  refreshSession,
  setAccessToken,
  startNewSession,
  startSessionKeepAlive,
} from '@/lib/api';

import { rememberVenueCode } from './venueCodeStorage';

/**
 * Boot retries when the server could not be reached. Doubling from 1s, capped
 * so a venue waiting out a deploy (or a phone finding its network) is back
 * within a quarter-minute of the server returning.
 */
const BOOT_RETRY_FIRST_MS = 1_000;
const BOOT_RETRY_MAX_MS = 15_000;

/**
 * A re-read of `/auth/me` (`reloadSession`) that has not answered in this long
 * is abandoned, as a refresh is (`lib/api`): a phone asleep mid-request can
 * leave a fetch pending for good, and every later re-read shares the one in
 * flight — the banner would stop counting down, and a saved forced change
 * would never open the app. A boot pass's read (the boot's and Try again's)
 * has it too: the boot would sit behind the loader for good, and Try again's
 * button would hold.
 */
const SESSION_READ_DEADLINE_MS = 20_000;

export interface SignedInEmployee {
  id: string;
  name: string;
  username: string;
  /** Permission keys granted to this employee. Owners receive the full set. */
  permissions: readonly string[];
  isOwner: boolean;
}

export interface VenueBranding {
  id: string;
  name: string;
  /**
   * The venue code staff type at sign-in (`Venue.slug`). `/auth/me` has
   * always sent it; read since the EclipseGG platform (2026-10-19) so the
   * browser can learn its code — see `fetchSession`.
   */
  slug: string;
  logoUrl: string | null;
  slogan: string | null;
  currencySymbol: string;
  /** Optional per-venue accent overrides applied to the theme at runtime. */
  primaryColor: string | null;
  secondaryColor: string | null;
}

/** What `GET /auth/me` answers: who, where, and the venue's service state. */
export interface StaffSession {
  employee: SignedInEmployee;
  venue: VenueBranding;
  /** The subscription warning; null unless the paid-until date is a week or less away. */
  serviceNotice: ServiceNotice | null;
  /** A password the seller set (or reset) that must be replaced before anything else. */
  mustChangePassword: boolean;
}

/**
 * `'suspended'`: the venue's EclipseGG service is suspended (EclipseGG
 * platform, 2026-10-19: "warn, then suspend"). The server refuses every staff
 * request with 403 `venue_suspended` until the seller reactivates it — and
 * keeps the cookie on purpose, so this is not `'anonymous'`.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'suspended';

interface AuthState {
  employee: SignedInEmployee | null;
  venue: VenueBranding | null;
  status: AuthStatus;
  /**
   * The server's count of days left on the venue's subscription, for the
   * banner. Counted there, in the venue's own time zone, because a till's
   * clock drifts; re-read on every renewal.
   */
  serviceNotice: ServiceNotice | null;
  /** True only when `/auth/me` says so: the app shows nothing but the change form. */
  mustChangePassword: boolean;
}

export interface AuthContextValue extends AuthState {
  /**
   * `venueCode` as typed. Blank sends no code at all, which the server reads
   * as LEGACY_DEFAULT_VENUE_SLUG while that deploy-day fallback is set, and
   * refuses with `venue_code_required` once it is not.
   */
  signIn: (username: string, password: string, venueCode: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** True when the signed-in employee holds the permission. Owners always pass. */
  can: (permission: string) => boolean;
  /**
   * The suspended screen's Try again: the boot sequence once — refresh, then
   * `/auth/me` — landing signed in after a reactivation, back on the suspended
   * screen while it lasts. A sign-out (or anything else that changes whose
   * session the screen shows) that lands while it runs wins: its answer is
   * dropped, teaches the browser no code and leaves none of its token behind.
   * Never rejects.
   */
  retry: () => Promise<void>;
  /**
   * Re-read `/auth/me` (and learn the venue code from it) while signed in:
   * after each renewal, and once a forced password change is saved. Callers
   * that overlap share one read. Resolves to the answer it put on screen, or
   * null when it put none: not signed in, the read failed or ran out of time,
   * or a sign-out, a sign-in or a suspension landed while it was in flight.
   * Never rejects.
   */
  reloadSession: () => Promise<StaffSession | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SIGNED_OUT: AuthState = {
  employee: null,
  venue: null,
  status: 'anonymous',
  serviceNotice: null,
  mustChangePassword: false,
};

/** The signed-in state `/auth/me` describes. Only an explicit `true` forces a password change. */
function sessionState(session: StaffSession): AuthState {
  return {
    employee: session.employee,
    venue: session.venue,
    status: 'authenticated',
    serviceNotice: session.serviceNotice ?? null,
    mustChangePassword: session.mustChangePassword === true,
  };
}

/**
 * Sign-in's read of `/auth/me`, and where the browser learns its venue code
 * (EclipseGG platform, 2026-10-19). The code is learnt at sign-in AND at every
 * boot (the boot learns it from the answer it puts on screen, `settleBoot`),
 * so a till that was already signed in when codes shipped — the 30-day sliding
 * cookie keeps most of them signed in — fills in `gc.staff.venueCode` without
 * anyone typing it, and a code the platform renames is re-learnt the same way
 * (tokens carry the venue id, never the code). Only by a browser still signed
 * in, though: a boot reads `/auth/me` only after a refresh succeeds, so a
 * browser signed out when its code was renamed keeps the old one until a
 * sign-in with the new one teaches it (which is why LoginPage opens a
 * remembered code after a refusal). Learnt from the server's answer, never
 * from the field, so a typo never sticks.
 */
async function fetchSession(): Promise<StaffSession> {
  const session = await api.get<StaffSession>('/auth/me');
  rememberVenueCode(session.venue.slug);
  return session;
}

type BootOutcome =
  | { kind: 'session'; session: StaffSession }
  | { kind: 'anonymous' }
  | { kind: 'suspended' }
  | { kind: 'unreachable' };

/**
 * What a boot pass that failed means for the screen: only a 401 means "not
 * signed in", only a 403 `venue_suspended` means "suspended", and anything
 * else is the network or a deploy.
 */
function failedBoot(error: unknown): BootOutcome {
  if (isSessionRejected(error)) return { kind: 'anonymous' };
  if (isVenueSuspended(error)) return { kind: 'suspended' };
  return { kind: 'unreachable' };
}

/**
 * Whose session the screen shows, as far as an answer still in flight is
 * concerned: a re-read of `/auth/me`, a Try again, or the boot. A new one is
 * made, never an old one reused, wherever that may change: a sign-in (from the
 * moment its answer lands), a sign-out, a refused session, a suspension, a boot
 * pass that settles. A re-read, a Try again and the boot each belong to the
 * epoch they started in and land only while that epoch is still the current
 * one. So an answer that outlived a sign-out (or a sign-out and somebody
 * else's sign-in on the same till) is dropped instead of putting the previous
 * employee back on screen, and a new epoch never waits on an old one's read.
 * `lib/api` keeps the same rule one level down (`startNewSession`): a refresh
 * that lands in a later session adopts no token and announces nothing.
 */
interface SessionEpoch {
  signedIn: boolean;
  /** This epoch's `/auth/me` re-read in flight, shared by every caller (see `reloadSession`). */
  reload: Promise<StaffSession | null> | null;
}

function newEpoch(signedIn: boolean): SessionEpoch {
  return { signedIn, reload: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...SIGNED_OUT, status: 'loading' });

  /** See `SessionEpoch`. */
  const epoch = useRef<SessionEpoch>(newEpoch(false));

  /**
   * Show what a boot pass found, learning the venue code from a session it
   * puts on screen (and only then: see `bootPass`). False only when the server
   * could not be reached, which is the one outcome worth trying again by
   * itself.
   */
  const settleBoot = useCallback((outcome: BootOutcome): boolean => {
    switch (outcome.kind) {
      case 'session':
        rememberVenueCode(outcome.session.venue.slug);
        epoch.current = newEpoch(true);
        setState(sessionState(outcome.session));
        return true;
      case 'anonymous':
        epoch.current = newEpoch(false);
        setAccessToken(null);
        setState(SIGNED_OUT);
        return true;
      case 'suspended':
        epoch.current = newEpoch(false);
        // Never onto the sign-in page, as the suspended listener below.
        setState((current) =>
          current.status === 'anonymous' ? current : { ...current, status: 'suspended' },
        );
        return true;
      case 'unreachable':
        return false;
    }
  }, []);

  /**
   * One pass of the boot sequence, for the boot and for Try again: mint an
   * access token from the cookie, then read who and where. Every failure is
   * sorted into what the screen should do next (`failedBoot`). It belongs to
   * the epoch it is given — the one the boot began in, or the one Try again
   * was pressed in — and resolves to null, leaving the screen to whoever
   * changed it, once that epoch is no longer the current one.
   *
   * Sign out is never held while Try again runs, and the sign-in form shows
   * while the boot is still out (`/login` renders during `'loading'`), so a
   * sign-out or a sign-in can land while a pass is out: its refresh held by a
   * flapping uplink, say, and answered after. An answer that outlived its
   * epoch is dropped: a 403 would put the suspended screen back over the
   * sign-in page, a 401 would sign out the person who had just signed in, and
   * a 200 would put the person who had just signed out (or the till's
   * previous person) back on screen. Its read is its own, not
   * `fetchSession`'s: it has the re-read's deadline, and the code is learnt
   * only from an answer `settleBoot` applies.
   */
  const bootPass = useCallback(async (startedIn: SessionEpoch): Promise<BootOutcome | null> => {
    if (epoch.current !== startedIn) return null;
    let outcome: BootOutcome;
    try {
      const minted = await refreshSession();
      if (epoch.current !== startedIn) {
        // Whoever the screen shows now, it is not the session this refresh
        // renewed, so the token it adopted goes, if it is still held. (One
        // that landed after a sign-in or a sign-out adopted nothing: lib/api
        // refused it as another session's.)
        if (getAccessToken() === minted) setAccessToken(null);
        return null;
      }
      const session = await api.get<StaffSession>('/auth/me', deadline(SESSION_READ_DEADLINE_MS));
      outcome = { kind: 'session', session };
    } catch (error) {
      outcome = failedBoot(error);
    }
    // Dropped as above. A sign-out that lands while the read is out clears
    // the token itself, and a refusal heard meanwhile was announced already.
    return epoch.current === startedIn ? outcome : null;
  }, []);

  // Restore an existing session on boot. The refresh token lives in an
  // httpOnly cookie, so the access token is re-minted rather than read here.
  //
  // Goes through `refreshSession` rather than posting to `/auth/refresh`
  // directly: the server rotates the cookie on use, and this effect runs twice
  // under StrictMode. Two direct posts meant the second one spent an already
  // rotated token, 401'd, and signed the user out on every page load.
  //
  // Only a 401 means "not signed in". Opening the app while the server is
  // restarting for a deploy, or on a phone still finding its network, used to
  // land on the sign-in page with a perfectly good cookie — a sign-out in all
  // but name. Those now wait and try again. A suspended venue does NOT: it
  // would answer the same 403 for as long as the suspension lasts, so the app
  // shows the suspended screen once and waits for someone to press Try again.
  //
  // The boot belongs to the epoch it began in (EclipseGG platform 2 review,
  // 2026-10-20): a sign-in from the form that shows meanwhile, or a refusal
  // its own requests heard, ends it — no pass lands after that, and none is
  // tried again (see `bootPass`).
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    const startedIn = epoch.current;

    const boot = async () => {
      const outcome = await bootPass(startedIn);
      if (cancelled || outcome === null || settleBoot(outcome)) return;
      const delay = Math.min(BOOT_RETRY_MAX_MS, BOOT_RETRY_FIRST_MS * 2 ** attempt);
      attempt += 1;
      retryTimer = setTimeout(() => void boot(), delay);
    };

    void boot();

    return () => {
      cancelled = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [bootPass, settleBoot]);

  // The server refused this browser's session mid-shift (signed out elsewhere,
  // password changed on another device, account switched off): show the
  // sign-in page once, instead of a screen that silently keeps failing.
  useEffect(
    () =>
      onSessionEnded(() => {
        epoch.current = newEpoch(false);
        setState(SIGNED_OUT);
      }),
    [],
  );

  // The venue was suspended mid-shift: every page's next request says so, and
  // the first one to hear it parks the app on the suspended screen. Who and
  // where are kept — the session is intact, and Try again resumes it. Never
  // from the sign-in page: a refusal still in flight from before a sign-out
  // has no session left to park, and the form words the suspension itself.
  useEffect(
    () =>
      onVenueSuspended(() => {
        // Whatever the screen shows next, no re-read already in flight lands
        // on it: a suspended screen stays put, and so does the sign-in page.
        epoch.current = newEpoch(false);
        setState((current) =>
          current.status === 'suspended' || current.status === 'anonymous'
            ? current
            : { ...current, status: 'suspended' },
        );
      }),
    [],
  );

  // While signed in, renew before the access token lapses and on every wake,
  // so a screen left open all shift — or a phone pulled from a pocket — never
  // meets an expired token in the first place. Only while signed in: a
  // suspended venue's refresh is refused, so the keep-alive stops with it.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    return startSessionKeepAlive();
  }, [state.status]);

  const reloadSession = useCallback((): Promise<StaffSession | null> => {
    const startedIn = epoch.current;
    if (!startedIn.signedIn) return Promise.resolve(null);
    // A re-read whose own 401 is refreshed announces that renewal, which asks
    // for a re-read: sharing the one in flight is what stops that loop.
    if (startedIn.reload) return startedIn.reload;
    // Its own read, not `fetchSession`'s: it has a deadline, and it learns the
    // venue code only from an answer it applies.
    const reload: Promise<StaffSession | null> = api
      .get<StaffSession>('/auth/me', deadline(SESSION_READ_DEADLINE_MS))
      .then(
        (session) => {
          // Only onto the session it was asked about: a sign-out, a sign-in
          // or a suspension that landed while this was in flight wins.
          if (epoch.current !== startedIn) return null;
          rememberVenueCode(session.venue.slug);
          setState(sessionState(session));
          return session;
        },
        () => {
          // A refused session and a suspended venue were each announced by
          // `request()` already; anything else is the network or the
          // deadline, and the screen keeps what it has until asked again.
          return null;
        },
      )
      .finally(() => {
        startedIn.reload = null;
      });
    startedIn.reload = reload;
    return reload;
  }, []);

  // Each renewal re-reads `/auth/me`, so the subscription banner counts down
  // (and a cleared must-change flag lands) on a screen left open for days.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    return onSessionRenewed(() => void reloadSession());
  }, [state.status, reloadSession]);

  // The suspended screen's Try again: one boot pass in the epoch it was
  // pressed in, so a sign-out that lands while it is out wins (`bootPass`).
  const retry = useCallback(async () => {
    const outcome = await bootPass(epoch.current);
    if (outcome) settleBoot(outcome);
  }, [bootPass, settleBoot]);

  const signIn = useCallback(async (username: string, password: string, venueCode: string) => {
    // Trimmed only: the server lowercases, so a phone's capital first letter
    // never fails a sign-in. A blank code is left out of the body, exactly as
    // every sign-in was sent before codes existed: while the server's
    // deploy-day fallback (LEGACY_DEFAULT_VENUE_SLUG) is set, that still works.
    const code = venueCode.trim();
    const { accessToken } = await api.post<{ accessToken: string }>('/auth/login', {
      username,
      password,
      ...(code ? { venueSlug: code } : {}),
    });
    // A new session from the moment the answer lands, here and in lib/api:
    // nothing still out from before — a boot pass, a signed-out person's
    // renewal or Try again — lands on it, adopts a token over it or
    // announces anything into it.
    startNewSession();
    epoch.current = newEpoch(false);
    setAccessToken(accessToken);
    const session = await fetchSession();
    epoch.current = newEpoch(true);
    setState(sessionState(session));
  }, []);

  const signOut = useCallback(async () => {
    // From the press on: no re-read still in flight puts this person back,
    // and no answer still out from their session adopts or announces
    // anything (lib/api).
    startNewSession();
    epoch.current = newEpoch(false);
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setState(SIGNED_OUT);
    }
  }, []);

  const can = useCallback(
    (permission: string) => {
      const employee = state.employee;
      if (!employee) return false;
      if (employee.isOwner) return true;
      return employee.permissions.includes(permission);
    },
    [state.employee],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, can, retry, reloadSession }),
    [state, signIn, signOut, can, retry, reloadSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
